/** @graphql cc **/

import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data';
import { usePageContentId } from '@confluence/page-context';

import {
	ConfluenceContentAccessRequestStatus,
	type useAccessRequestsQuery,
	type useAccessRequestsQueryVariables,
} from './__types__/useAccessRequestsQuery';

export const ACCESS_REQUESTS_QUERY = gql`
	query useAccessRequestsQuery(
		$after: String
		$cloudId: ID!
		$contentId: ID!
		$first: Int = 10
		$status: ConfluenceContentAccessRequestStatus = PENDING
	) {
		confluence_contentAccessRequestByStatus(
			after: $after
			cloudId: $cloudId
			contentId: $contentId
			first: $first
			status: $status
		) {
			nodes {
				user {
					displayName
					emails {
						isPrimary
						value
					}
					photos {
						isPrimary
						value
					}
				}
				accessRequestedAaid
				requestAccessType
				status
				id
			}
			pageInfo {
				endCursor
				hasNextPage
				startCursor
			}
		}
	}
`;

// This is its own hook primarily to cache the results on USD load
// and ensure the Requests button shows as early as possible
export const useAccessRequests = (
	fetchPolicy: WatchQueryFetchPolicy,
	source: string,
	skipOverride: boolean = false,
	status: keyof typeof ConfluenceContentAccessRequestStatus = 'PENDING',
) => {
	const [contentId = ''] = usePageContentId();
	const { cloudId, edition } = useSessionData();
	const skip = !contentId || edition === 'FREE' || skipOverride;

	const { data, loading, error, fetchMore } = useQuery<
		useAccessRequestsQuery,
		useAccessRequestsQueryVariables
	>(ACCESS_REQUESTS_QUERY, {
		variables: {
			cloudId,
			contentId,
			status: ConfluenceContentAccessRequestStatus[status],
		},
		skip,
		fetchPolicy,
		notifyOnNetworkStatusChange: true,
	});

	const requests = data?.confluence_contentAccessRequestByStatus?.nodes ?? [];
	const pageInfo = data?.confluence_contentAccessRequestByStatus?.pageInfo;
	const endCursor = pageInfo?.endCursor ?? null;
	const hasNextPage = pageInfo?.hasNextPage ?? false;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'accessRequestsQuery',
					source,
					attributes: {
						contentId,
						errorMessage: error.message,
					},
				},
			}).fire();
		}
	}, [error, source, contentId, createAnalyticsEvent]);

	return {
		requests,
		endCursor,
		hasNextPage,
		loading,
		error,
		fetchMore,
		contentId,
		hasFullyLoaded: !!data && !loading,
	};
};
